<template>
  <BittsModal
    :loading="!ready"
    title="Delete Data Source"
    description="Are you sure you want to delete your connection? Removing this data source will permanently delete all associated populations and sharing rules. This cannot be undone."
    primary-button-text="Yes, Continue"
    primary-button-type="danger"
    secondary-button-text="Discard"
    :width="400"
    :action-loading="deleting"
    :disabled="deleting || !ready || !canManageDataSources"
    @action="deleteFeed"
    @closed="closeModal"
  />
</template>

<script setup lang="ts">
import { BittsModal } from '@crossbeam/bitts';

import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useAwsPartnerCentral from '@/composables/useAwsPartnerCentral';
import { captureException } from '@/errors';
import {
  allReady,
  useDataSharesStore,
  useFeedsStore,
  useFileUploadsStore,
  useFlashesStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';

type Props = {
  feedId: number;
  sourceId?: number;
  cancelDestination: string;
};
const { feedId, sourceId, cancelDestination } = defineProps<Props>();

const router = useRouter();
const feedsStore = useFeedsStore();
const sourcesStore = useSourcesStore();
// AWS feeds live outside FeedsStore on an offline-partner org: refresh them so
// a deleted one clears from the list, and resolve the org uuid for the delete.
const { refreshAwsPartnerCentral, getAwsFeedById, awsPartnerCentralOrgUuid } =
  useAwsPartnerCentral();

const { hasPermission } = useAuth();

const canManageDataSources = computed(() =>
  hasPermission('manage:data-sources'),
);

const deleting = ref(false);
const ready = allReady(sourcesStore, feedsStore);

async function deleteFeed() {
  const flashesStore = useFlashesStore();
  const populationsStore = usePopulationsStore();
  const dataSharesStore = useDataSharesStore();
  const fileUploadsStore = useFileUploadsStore();

  deleting.value = true;
  try {
    if (sourceId) {
      await sourcesStore.deleteSource(sourceId);
    } else {
      // Warm the AWS cache before choosing the delete path so a cold cache
      // (e.g. a deep-link straight to the delete route) doesn't misroute an AWS
      // delete to the customer's own org. Catch so an AWS outage can't block
      // deleting non-AWS feeds.
      await refreshAwsPartnerCentral().catch(captureException);
      if (getAwsFeedById(feedId)) {
        // AWS feed: the delete must target the offline-partner org. Without its
        // uuid the request would hit the customer's own org, so bail out rather
        // than delete against the wrong org.
        const uuid = await awsPartnerCentralOrgUuid();
        if (!uuid) {
          throw new Error(
            'Missing AWS Partner Central org uuid; aborting feed delete',
          );
        }
        await feedsStore.removeFeed(feedId, uuid);
      } else {
        await feedsStore.removeFeed(feedId);
      }
    }
    await Promise.all([
      dataSharesStore.refreshDataSharesStore(),
      populationsStore.refreshPopulationsStore(),
      feedsStore.refreshFeedsStore(),
      sourcesStore.refreshSourcesStore(),
      fileUploadsStore.refreshFileUploadsStore(),
      // Catch: a failed post-delete refetch shouldn't report an error on an
      // otherwise-successful delete.
      refreshAwsPartnerCentral().catch(captureException),
    ]);
    flashesStore.addSuccessFlash({ message: 'Data deletion initiated' });
    await router.push({ name: 'data-sources' });
  } catch (err) {
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'There was an issue with your delete',
      description: 'If the error persists contact support@crossbeam.com',
    });
    closeModal();
  }
}

async function closeModal() {
  deleting.value = false;
  await router.push({ path: cancelDestination });
}
</script>
